/**
 * Section-agnostic render component for the unified entity performance report.
 *
 * `Report` is the single shared view rendered by *both* the assets
 * (`AssetUsage`) and systems (`SystemReporting`) sections. It owns the whole
 * render + data-fetch path: it calls {@link useReport} internally from the
 * `scope` / `entityId` (+ optional window/frequency) props, so the wiring tasks
 * (11.1 / 11.2) only have to resolve `scope` + `entityId` and render
 * `<Report scope=… entityId=… />` — everything else (KPIs, graphs, wide table,
 * loading / error / unavailable states) is handled here.
 *
 * The report the hook returns is *render-complete*: the backend bakes in human
 * labels and units, keys rows by `displayKey`, and gates availability. This
 * component is therefore a pure presenter — it never re-derives numbers.
 *
 * Regions are isolated for partial-render resilience: a KPI, a graph, or the
 * table that throws while rendering is caught by a per-region error boundary so
 * the *rest* of the report still renders rather than blanking the whole view
 * (Requirement 10.7). The repo has no shared `ErrorBoundary` in the `shared/`
 * workspace (only hub-level ones), so a small local boundary is defined here.
 *
 * Requirements: 10.1 (shared render surface), 10.3 (KPIs + graphs + wide
 * table), 10.7 (partial-render resilience), 8.1 (availability / "not
 * available" state).
 */
import React, { useEffect, useMemo, useRef, useState } from "react"
import { format, parseISO } from "date-fns"
import { Box, Button, chakra, Flex, Heading, HStack, Icon, Skeleton, Text, VStack } from "@chakra-ui/react"
import {
  FaBolt,
  FaCheck,
  FaClock,
  FaDownload,
  FaGasPump,
  FaLeaf,
  FaLink,
  FaShareNodes,
} from "react-icons/fa6"
import {
  CreateShareLinkDocument,
  GetShareLinkDocument,
  RevokeShareLinkDocument,
  UpdateShareLinkDocument,
} from "@hydrologiq/gql-experience/graphql"
import type {
  CreateShareLinkMutation,
  CreateShareLinkMutationVariables,
  GetShareLinkQuery,
  GetShareLinkQueryVariables,
  RevokeShareLinkMutation,
  RevokeShareLinkMutationVariables,
  UpdateShareLinkMutation,
  UpdateShareLinkMutationVariables,
} from "@hydrologiq/gql-experience/graphql"
import { useMutationWithEndpoint, useQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"
import Popover from "@hydrologiq/shared/chakra/Popover"
import Dialog from "@hydrologiq/shared/chakra/Dialog"
import { Switch } from "@hydrologiq/shared/chakra/Switch"
import { ActionButton } from "@hydrologiq/shared/content/components/Button"
import Informative from "@hydrologiq/shared/Informative"
import Figure from "@hydrologiq/shared/figures/generic/Figure"
import FigureContent from "@hydrologiq/shared/figures/generic/FigureContent"
import FigureGrid from "@hydrologiq/shared/figures/generic/FigureGrid"
import TrendIndicator, { type TrendIndicatorProps } from "@hydrologiq/shared/figures/generic/TrendIndicator"
import { Chart, useChart } from "@chakra-ui/charts"
import { Bar, BarChart, CartesianGrid, LabelList, Legend, Tooltip, XAxis, YAxis, type LabelProps } from "recharts"
import { formatSeriesLabel } from "./chartLabels"
import { colorForSeries, LABEL_TEXT_COLOR, SEMANTIC_COLORS } from "./reportColors"
import { AVG_CSS, CAPTURE, CAPTURE_CHART_TEXT_CSS, CAPTURE_HEADER_MIN_HEIGHT, TOTAL_CSS } from "./chartHeaderTags"
import { unitTooltip } from "./ChartTooltip"
import PhaseBalanceChart from "./PhaseBalanceChart"
import RunHoursChart, { legForKey } from "./RunHoursChart"
import ReportResultsTable from "./ReportResultsTable"
import { toReportScope, useReport, type Report as ReportData, type ReportScopeInput } from "./useReport"
import { DEFAULT_FREQUENCY, type Frequency } from "./frequency"
import type { Cadence } from "./periodOptions"
import { reportDateFormatter } from "./reportDateFormat"
import { CHART_CARD_ATTR, CHART_SECTION_ATTR, CHART_TITLE_ATTR } from "./rasterizeCharts"
import { useReportPdf, type ReportPdfContent } from "./useReportPdf"

/**
 * Narrowed element types derived from the resolved report so this component
 * stays in lockstep with the `getReport` query selection (no separate hand-kept
 * types). `ReportData` is `GetReportQuery["getReport"]`.
 */
type Kpi = ReportData["kpis"][number]
type Graph = ReportData["graphs"][number]
/** Wide-table rows are a JSON scalar (render-complete dicts keyed by displayKey). */
type Row = Record<string, unknown>

/**
 * Series keys emitted by the `phase_share` reducer. A graph whose series are
 * these fields is routed to {@link PhaseBalanceChart} (stacked-bar variant)
 * rather than the default per-series bar chart.
 */
const PHASE_SHARE_KEYS = new Set([
  "phase_share_l1",
  "phase_share_l2",
  "phase_share_l3",
  // Class-scoped genset phase shares (the "Genset phase balance" graph). Routed
  // to the same stacked PhaseBalanceChart — they are three-phase shares that
  // differ from the role-scoped keys only in source (class='genset').
  "genset_phase_share_l1",
  "genset_phase_share_l2",
  "genset_phase_share_l3",
])


/**
 * Default visibility of the on-screen results table. Parked (hidden) for now:
 * the table has been aligned to the Download view's structure (see
 * ReportResultsTable, which mirrors AssetHybridReporting's grouped/active-column/
 * 2-decimal layout) so it's ready to re-enable, but the report currently shows
 * KPIs + graphs only. This is the *default* — a caller can flip it on per-mount
 * via the {@link ReportProps.showResultsTable} prop without a source edit, and
 * flipping this constant changes the default for every caller.
 */
const SHOW_RESULTS_TABLE = false

/**
 * The branded document chrome (the scope- and cadence-aware performance-report
 * header, the reporting-period / issued lines, and the diesel-baseline savings
 * caption) heads the A4 page. Flip to `false` to hide it.
 */
const SHOW_REPORT_CHROME = true

/**
 * Default visibility of the per-KPI trend indicators (the up/down/flat arrow
 * with the signed change beneath a KPI's value). On by default (CES mockup
 * polish): when the backend returns a `trend` for a KPI, its arrow + signed
 * change renders. This is the *default* — a caller can flip it off per-mount via
 * the {@link ReportProps.showTrends} prop without a source edit, and flipping
 * this constant changes the default for every caller.
 */
const SHOW_TRENDS = true

export interface ReportProps {
  /** Entity kind the report targets (enum value or lowercase string). */
  scope: ReportScopeInput
  /** Identifier of the entity (asset / hybrid / system / installation). */
  entityId: string
  /** Optional ISO-8601 window start. Defaults (in the hook) to 7 days before `stop`. */
  start?: string
  /** Optional ISO-8601 window end. Defaults (in the hook) to now. */
  stop?: string
  /** Optional download granularity. Defaults to {@link DEFAULT_FREQUENCY}. */
  frequency?: Frequency
  /**
   * Show the per-KPI trend indicators (up/down/flat arrow + signed change).
   * Built but parked — off by default (see {@link SHOW_TRENDS}); pass `true` to
   * opt a caller in without a source edit + redeploy.
   */
  showTrends?: boolean
  /**
   * Show the on-screen results table beneath the graphs. Built but parked — off
   * by default (see {@link SHOW_RESULTS_TABLE}); pass `true` to opt a caller in
   * without a source edit + redeploy.
   */
  showResultsTable?: boolean
  /**
   * Optional controls rendered in the top-right of the branded header (e.g. the
   * cadence + period selectors from {@link ReportWithPeriodControls}). Rendered
   * inside the report surface across every state — loading, error, unavailable,
   * and loaded — so the selectors stay put rather than floating above the report.
   */
  controls?: React.ReactNode
  /**
   * Optional entity name used to build the header title (e.g. "Garic SES" →
   * "Garic SES performance report"). When omitted the title falls back to the
   * scope-based form ("Asset performance report"). The cadence is no longer
   * baked into the title (see {@link cadenceLabel}).
   */
  entityName?: string
  /**
   * Optional cadence label ("Daily" / "Weekly" / "Monthly") shown as the lead
   * of the period metadata line ("Weekly · 8 Sep – 14 Sep 2026"). When omitted
   * the period line shows just the date range.
   */
  cadenceLabel?: string
  /**
   * Optional cadence (the period selector: daily / weekly / monthly). Used only
   * for the PDF download filename's granularity token (weekly → "week", monthly
   * → "month", daily → none). The on-screen period line uses {@link cadenceLabel}.
   */
  cadence?: Cadence
  /**
   * Enable the "Download PDF" button in the header. When true, Report owns the
   * capture container ref, rasterises its charts, and exports a vector PDF named
   * from `entityName` + the selected period `start` + the cadence granularity
   * token. Off by default so a plain `<Report>` (e.g. embedded previews) has no
   * export button.
   */
  enablePdfDownload?: boolean
  /**
   * Opaque share token for the login-free public view (`PublicReportView`).
   * When supplied the report is fetched via `getPublicReport` (keyed by the
   * token, resolved server-side to its granted scope/entity) rather than the
   * authed `getReport`, and the operator Share action is hidden — a public
   * viewer must not re-share (Requirement 5.3).
   */
  shareToken?: string
}

/* -------------------------------------------------------------------------- */
/* Partial-render resilience                                                  */
/* -------------------------------------------------------------------------- */

interface RegionBoundaryProps {
  /** Region label used in the fallback message (e.g. "graph", "table"). */
  label: string
  children: React.ReactNode
}

interface RegionBoundaryState {
  hasError: boolean
}

/**
 * A minimal error boundary that isolates one region of the report. If a KPI,
 * graph, or table subtree throws while rendering, the boundary swaps in a small
 * "couldn't be displayed" placeholder for *that region only*, leaving the rest
 * of the report intact (Requirement 10.7).
 *
 * A class component is required here because React error boundaries can only be
 * expressed via `componentDidCatch` / `getDerivedStateFromError` — there is no
 * hook equivalent.
 */
class RegionBoundary extends React.Component<RegionBoundaryProps, RegionBoundaryState> {
  constructor(props: RegionBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): RegionBoundaryState {
    return { hasError: true }
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box py={4}>
          <Informative
            heading="Unavailable"
            text={`This ${this.props.label} couldn't be displayed.`}
          />
        </Box>
      )
    }
    return this.props.children
  }
}

/* -------------------------------------------------------------------------- */
/* KPI headline cards                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Category glyph rendered beside a headline card's heading, keyed by the KPI key
 * the resolver emits (`field + variant [+ _role]`, see `_kpi_key_for_card`).
 * Colours come from the shared palette (power light blue, fuel brown, carbon
 * green, runtime gray); a key with no entry simply renders without an icon.
 */
const KPI_ICONS: Record<string, { icon: React.ElementType; color: string }> = {
  power_average_load: { icon: FaBolt, color: SEMANTIC_COLORS.siteProfile },
  fuel_saved: { icon: FaGasPump, color: SEMANTIC_COLORS.fuel },
  co2e_saved: { icon: FaLeaf, color: SEMANTIC_COLORS.carbon },
  generator_runtime: { icon: FaClock, color: SEMANTIC_COLORS.neutral },
}

/**
 * Which way a change in each KPI is good news, keyed like {@link KPI_ICONS}. It
 * sets the trend colour, not the arrow: more load is neither good nor bad, more
 * saving is good, and more generator runtime is bad because it means less from
 * the battery and renewables. A key with no entry keeps the report default
 * (a rise reads green).
 */
const KPI_GOOD_DIRECTION: Record<string, TrendIndicatorProps["goodDirection"]> = {
  power_average_load: "neutral",
  fuel_saved: "increase",
  co2e_saved: "increase",
  generator_runtime: "decrease",
}

/**
 * Trend unit for a KPI. A percentage KPI's period-over-period change is measured
 * in *percentage points*, so its badge suffix reads `pp` (e.g. `↓3.4pp`) rather
 * than `%` — a level that is itself a percentage doesn't change by a "percent of
 * a percent". Value KPIs keep their own unit (the backend-baked `kpi.unit`).
 * Presentation-only: the backend delta/percent are unchanged.
 */
function trendUnit(kpi: Kpi): string | null | undefined {
  return kpi.unit === "%" ? "pp" : kpi.unit
}

/** Format a KPI's numeric value + unit for the card. `null` value → em dash. */
function formatKpiValue(kpi: Kpi): string {
  if (kpi.value == null) return "—"
  const rounded = Math.round(kpi.value * 10) / 10
  const value = kpi.unit ? `${rounded} ${kpi.unit}` : `${rounded}`
  // Savings cards carry their share of the diesel-only baseline: "160 L (80%)".
  return kpi.savedShare == null ? value : `${value} (${Math.round(kpi.savedShare)}%)`
}

/**
 * Per-direction arrow glyph for the baked PDF trend string, mirroring the
 * on-screen {@link TrendIndicator}'s report presentation (↑ up / ↓ down), except
 * that flat reads – rather than →: the PDF's Montserrat latin subset has no →
 * glyph. An unrecognised direction falls back to flat, matching the component's
 * `resolveTrendDirection`.
 */
const TREND_ARROW: Record<string, string> = {
  up: "↑",
  down: "↓",
  flat: "–",
}

/**
 * Bake a KPI's trend into a compact, pre-formatted string for the PDF lockstep —
 * the PDF equivalent of the on-screen {@link TrendIndicator} report badge. The
 * numbers are NOT re-derived: `kpi.trend.delta` / `.percent` / `.direction` are
 * read as already computed by the backend and only formatted here, reusing
 * {@link trendUnit} to detect the percentage-point case (`pp` for a `%` KPI).
 *
 * Two presentations, mirroring the on-screen component:
 *  - Percentage-point KPIs (`trendUnit === "pp"`): keep the signed *absolute* pp
 *    delta — e.g. `↑ +13.9 pp`. Never hidden (pp KPIs don't use `percent`). A
 *    delta rounding to flat/0 reads `– 0 pp`.
 *  - Value / ratio KPIs (kWh, L, kg, g/kWh, unitless): show the signed *relative*
 *    percent only — e.g. `↑ +8.1%` — dropping the absolute delta. Returns `null`
 *    when `kpi.trend.percent` is null (undefined relative change → nothing to
 *    show). A trend rounding to flat reads `– 0%`.
 *
 * Returns `null` when the KPI carries no trend at all, matching the on-screen
 * path which renders nothing for a null trend. The PDF string is plain text
 * (no colour) — the PDF component styles it.
 */
export function formatKpiTrend(kpi: Kpi): string | null {
  const trend = kpi.trend
  if (trend == null) return null
  const delta = Math.round(trend.delta * 10) / 10
  // Round-to-flat parity with the on-screen TrendIndicator: a delta that rounds
  // to 0 at display precision reads as flat (→) even when the backend
  // `direction` says "up"/"down" for a negligible non-zero delta.
  const isFlat = delta === 0
  const arrow = isFlat ? TREND_ARROW.flat : (TREND_ARROW[trend.direction] ?? TREND_ARROW.flat)
  const isPercentagePoint = trendUnit(kpi) === "pp"

  if (isPercentagePoint) {
    const sign = delta > 0 ? "+" : ""
    return `${arrow} ${sign}${delta} pp`
  }

  // Value / ratio KPI: relative percent only. Undefined relative change (percent
  // null) → render nothing, since the absolute delta is no longer shown.
  if (trend.percent == null) return null
  const percent = isFlat ? 0 : Math.round(trend.percent * 10) / 10
  const sign = percent > 0 ? "+" : ""
  return `${arrow} ${sign}${percent}%`
}

/**
 * Directional trend indicator rendered beneath a KPI's value. Delegates to the
 * shared generic {@link TrendIndicator} (its report presentation: up/down/flat
 * arrow + signed change), so the report reuses the same trend component as the
 * headline/figure cards rather than a bespoke copy.
 *
 * The `kpi.trend` shape (`{ delta, percent, direction }`) is structurally the
 * component's `TrendDatum`. Rendering is gated behind the in-code
 * {@link SHOW_TRENDS} toggle (on by default) and skipped entirely when the KPI
 * carries no trend (Requirements 6.4, 6.5). The `unit` is the presentation unit
 * for the signed delta — `pp` for a percentage KPI, else the KPI's own unit (see
 * {@link trendUnit}). `goodDirection` sets the colour (see {@link KPI_GOOD_DIRECTION}).
 */
function KpiTrend({
  trend,
  unit,
  goodDirection,
  show,
}: {
  trend: Kpi["trend"]
  unit?: string | null
  goodDirection?: TrendIndicatorProps["goodDirection"]
  show: boolean
}) {
  if (!show) return null
  return <TrendIndicator trend={trend} unit={unit} goodDirection={goodDirection} />
}

function KpiCards({ kpis, showTrends }: { kpis: Kpi[]; showTrends: boolean }) {
  if (kpis.length === 0) return null
  return (
    <FigureGrid>
      {kpis.map((kpi) => {
        const glyph = KPI_ICONS[kpi.key]
        return (
          <RegionBoundary key={kpi.key} label="metric">
            <Box height="100%">
              <Figure
                heading={kpi.label}
                headingIcon={glyph && <Icon as={glyph.icon} color={glyph.color} boxSize="13px" />}
              >
                <FigureContent.Root>
                  <Flex align="baseline" gap={2} width="100%" wrap="wrap">
                    <FigureContent.Value>{formatKpiValue(kpi)}</FigureContent.Value>
                    <KpiTrend
                      trend={kpi.trend}
                      unit={trendUnit(kpi)}
                      goodDirection={KPI_GOOD_DIRECTION[kpi.key]}
                      show={showTrends}
                    />
                  </Flex>
                  {kpi.secondary && <FigureContent.SubText>{kpi.secondary}</FigureContent.SubText>}
                </FigureContent.Root>
              </Figure>
            </Box>
          </RegionBoundary>
        )
      })}
    </FigureGrid>
  )
}

/* -------------------------------------------------------------------------- */
/* Graphs                                                                     */
/* -------------------------------------------------------------------------- */

/** True when the graph's series are the phase-share fields (→ stacked bars). */
function isPhaseShareGraph(graph: Graph): boolean {
  const series = graph.series ?? []
  return series.length > 0 && series.every((s) => PHASE_SHARE_KEYS.has(s.key))
}

/**
 * True when the graph's series are the run-hours legs (`engine_hours` and/or
 * `battery_only_hours`) → the stacked {@link RunHoursChart}. A single-leg graph
 * (genset-only or battery-only entity) matches too, since the absent leg is
 * pruned from the series by the backend.
 */
export function isRunHoursGraph(graph: Graph): boolean {
  const series = graph.series ?? []
  return series.length > 0 && series.every((s) => legForKey(s.key) !== null)
}

interface GraphDatum {
  start: string
  [seriesKey: string]: string | number | null
}

/**
 * Fold a graph's series into one datum per time bucket keyed by series key,
 * mirroring `ReportingChart`'s data shaping. Absent points stay `null` (a gap)
 * rather than being coerced to 0.
 */
function toGraphData(graph: Graph): GraphDatum[] {
  const byBucket = new Map<string, GraphDatum>()
  for (const s of graph.series ?? []) {
    for (const point of s.points ?? []) {
      const existing = byBucket.get(point.time) ?? { start: point.time }
      existing[s.key] = point.value == null ? null : Math.round(point.value * 10) / 10
      byBucket.set(point.time, existing)
    }
  }
  return Array.from(byBucket.values()).sort((a, b) => a.start.localeCompare(b.start))
}

/**
 * Period-average (mean) and total (sum) of a graph's plotted series, computed
 * over the present (non-null) point values across all of the graph's series —
 * the same points the backend already returns, so the reference line and total
 * stay in lockstep with the plotted bars (no client-side re-derivation of
 * numbers, only aggregation).
 *
 * `mean` is `null` when the graph has no present values, so callers can skip the
 * `ReferenceLine` for an empty window rather than drawing one at 0.
 */
function graphSeriesStats(graph: Graph): { mean: number | null; total: number } {
  const values: number[] = []
  for (const s of graph.series ?? []) {
    for (const point of s.points ?? []) {
      if (point.value != null) values.push(point.value)
    }
  }
  if (values.length === 0) return { mean: null, total: 0 }
  const total = values.reduce((acc, v) => acc + v, 0)
  return { mean: total / values.length, total }
}

/** Round to one decimal place, matching the per-bucket value shaping. */
function round1(value: number): number {
  return Math.round(value * 10) / 10
}



/**
 * The type recharts' `LabelList` `content` prop expects for a render function.
 * We read only a handful of fields (see {@link BarLabelProps}); this alias lets
 * the narrower render callback be cast to what recharts wants without pulling in
 * its full (and internally inconsistent) label props surface.
 */
type LabelListContent = (props: LabelProps) => React.ReactElement | null

/**
 * Props recharts hands a `LabelList` `content` renderer for one bar. The bar's
 * box is `(x, y)` top-left with `width`/`height`; `value` is that bar's datum.
 * All are optional/loosely typed by recharts, so they're narrowed here.
 */
interface BarLabelProps {
  x?: string | number
  y?: string | number
  width?: string | number
  height?: string | number
  value?: string | number | null
  /** recharts 3 nests the bar box here; read as a fallback to the flat fields. */
  viewBox?: { x?: number; y?: number; width?: number; height?: number }
}

/** Coerce a recharts label coordinate (which may be a string) to a number. */
function toNumber(value: string | number | undefined): number {
  return typeof value === "number" ? value : Number(value ?? 0)
}

/**
 * A single bar's value label, used for both single- and multi-series graphs.
 *
 * - Single series (`rotate=false`): a horizontal label centred just above the
 *   bar — the classic bar-value position.
 * - Multi series (`rotate=true`): the bars are narrow, so the label is rotated
 *   90° and drawn *inside* the bar column near its top, coloured to match the
 *   series. Vertical text fits a narrow column where a horizontal number would
 *   overlap its neighbours.
 *
 * Absent buckets (`value == null`) render nothing, so gaps stay unlabelled.
 */
function BarValueLabel({
  x,
  y,
  width,
  value,
  viewBox,
  rotate,
  color,
  format,
}: BarLabelProps & { rotate: boolean; color: string; format: (value: number) => string }) {
  if (typeof value !== "number") return null
  // Prefer the flat coordinates (LabelList entry) and fall back to the nested
  // viewBox (recharts 3 label content) so the label positions either way.
  const bx = toNumber(x ?? viewBox?.x)
  const by = toNumber(y ?? viewBox?.y)
  const bw = toNumber(width ?? viewBox?.width)
  const text = format(value)

  if (rotate) {
    // Centre of the bar column; sit a little below the bar top so the rotated
    // text reads from inside the bar upward.
    const cx = bx + bw / 2
    const cy = by + 4
    return (
      <text
        x={cx}
        y={cy}
        fill={color}
        fontSize={9}
        fontWeight={600}
        textAnchor="end"
        dominantBaseline="central"
        transform={`rotate(-90, ${cx}, ${cy})`}
      >
        {text}
      </text>
    )
  }

  // Horizontal label centred above the bar top.
  return (
    <text x={bx + bw / 2} y={by - 4} fill="#545454" fontSize={10} textAnchor="middle">
      {text}
    </text>
  )
}

/**
 * Default (non-phase) graph renderer: a grouped bar chart with one bar per
 * series, mirroring `ReportingChart`'s Chakra/recharts setup (X = point time,
 * Y labelled with the graph's axis unit). `ReportingChart` itself is coupled to
 * the legacy Reporting rows/`FIELD_CONFIG` API and can't render a `getReport`
 * `ReportGraph`, so a thin inline chart is used here for consistency.
 */
function ReportGraphChart({ graph, frequency }: { graph: Graph; frequency: Frequency }) {
  const data = toGraphData(graph)
  const unitForLabels = graph.axis ?? ""
  const series = (graph.series ?? []).map((s, i) => ({
    name: s.key,
    color: colorForSeries(s.key, i),
    label: formatSeriesLabel(s.label ?? s.key, unitForLabels),
  }))

  const chart = useChart<GraphDatum>({ data, series })
  // Sub-daily reports (e.g. a daily report bucketed hourly) get time-bearing
  // tick/hover labels so every bucket reads distinctly and the hour shows on
  // hover; daily-and-coarser reports keep the day-only label.
  const dateFormatter = reportDateFormatter(frequency)
  const unit = graph.axis ?? ""

  // Percentage graphs (e.g. Runtime) are pinned to a full 0–100 domain so a
  // low or all-zero window still reads on a proper percentage scale rather than
  // recharts auto-scaling to the data and making a flat/low series look full.
  const isPercent = unit === "%"
  const yDomain = isPercent ? ([0, 100] as const) : undefined

  const hasData = data.some((datum) =>
    series.some((s) => datum[s.name] != null),
  )

  // Period-average + per-graph total, over the present point values of the
  // plotted series. Only meaningful for a single additive, non-percentage
  // series (e.g. Fuel litres, Energy kWh) — summing/averaging across multiple
  // series or percentage shares is nonsensical, so the header stats are gated
  // to that case. Percentage graphs never show a total either.
  const { mean, total } = graphSeriesStats(graph)
  const showStats = series.length === 1 && !isPercent

  const withUnit = (value: number) => (unit ? `${round1(value)} ${unit}` : `${round1(value)}`)

  // Per-bar value labels, shown on screen and in the PDF. Suppressed only once a
  // chart is too dense to label legibly — the label count is buckets × series,
  // and beyond ~48 the rotated numbers collide even vertically. Single series
  // draw a horizontal label above each bar; multi-series bars are narrow, so
  // their labels rotate vertical to fit the column (see BarValueLabel).
  const labelDensity = data.length * Math.max(series.length, 1)
  // Per-bar value labels are hidden for now: they crowd dense charts and don't
  // add much over the axis. To re-enable, restore:
  //   series.length > 0 && labelDensity <= 48
  void labelDensity
  const showValueLabels = false
  const rotateLabels = series.length > 1

  return (
    <Box bg="card" boxShadow="card" borderRadius="card" p={4} width="100%" height="100%">
      {/* The VStack is a size container so the header tags size with the card,
          matching RunHoursChart (see chartHeaderTags.ts). */}
      <VStack
        pt={1}
        pb={2}
        gap={4}
        alignItems="flex-start"
        justifyContent="space-between"
        width="100%"
        height="100%"
        css={{ containerType: "inline-size" }}
      >
        {/* Header: heading on the left, tags right-aligned. The row wraps, so a
            narrow card drops the tags under the heading, still right-aligned. */}
        <Box
          width="100%"
          display="flex"
          flexWrap="wrap"
          alignItems="flex-start"
          justifyContent="space-between"
          columnGap={4}
          rowGap={2}
          css={{ [CAPTURE]: { minHeight: CAPTURE_HEADER_MIN_HEIGHT } }}
        >
          <Heading as="h3" size="sm" flex="1 1 auto" minWidth={0}>
            {graph.title}
          </Heading>
          {hasData && showStats && (
            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={2} marginLeft="auto" whiteSpace="nowrap">
              {mean != null && (
                <Box as="span" display="inline-flex" alignItems="center" gap="6px" css={AVG_CSS}>
                  {/* Colour swatch echoing the (now removed) in-chart average
                      marker, so "Avg" reads as a level rather than a bare number. */}
                  <Box as="span" width="10px" height="0" borderTopWidth="2px" borderTopStyle="dashed" borderTopColor="#2b2b2b" />
                  Avg {withUnit(mean)}
                </Box>
              )}
              <Box as="span" css={TOTAL_CSS}>
                Total {withUnit(total)}
              </Box>
            </Box>
          )}
        </Box>
        {!hasData ? (
          <Informative heading="No data available" text="No data available for the selected period." />
        ) : (
          <Chart.Root height="260px" chart={chart} css={CAPTURE_CHART_TEXT_CSS}>
            <BarChart data={chart.data} margin={{ top: 20, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid stroke={chart.color("border.muted")} vertical={false} />
              {/* `preserveStartEnd` guarantees a tick on the first and last
                  buckets (e.g. 00:00 and 23:00 for an hourly day) so the axis
                  always spans the full selected period; recharts thins the
                  middle ticks to avoid overlap. */}
              <XAxis
                axisLine={false}
                tickLine={false}
                dataKey={chart.key("start")}
                tickFormatter={dateFormatter}
                interval="preserveStartEnd"
                tick={{ fill: LABEL_TEXT_COLOR }}
              />
              <Tooltip
                cursor={{ fill: chart.color("bg.muted") }}
                animationDuration={0}
                content={unitTooltip(unit, dateFormatter)}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: LABEL_TEXT_COLOR }}
                domain={yDomain}
                tickFormatter={(value) => (unit ? `${value} ${unit}` : `${value}`)}
              />
              {chart.series.map((item) => (
                <Bar
                  key={item.name}
                  isAnimationActive={false}
                  dataKey={chart.key(item.name)}
                  fill={chart.color(item.color)}
                  name={String(item.label)}
                >
                  {/* Per-bar value label. Single-series graphs draw a horizontal
                      label above each bar; multi-series bars are narrow, so the
                      label is rotated vertical to fit its column and coloured to
                      match the series. Suppressed for dense charts (see
                      showValueLabels). `null`/absent buckets stay unlabelled. */}
                  {showValueLabels && (
                    <LabelList
                      dataKey={chart.key(item.name)}
                      // recharts' content-callback prop type is broader than the
                      // fields we read; narrow to BarLabelProps inside the render.
                      content={
                        ((props: BarLabelProps) => (
                          <BarValueLabel
                            x={props.x}
                            y={props.y}
                            width={props.width}
                            height={props.height}
                            value={props.value}
                            viewBox={props.viewBox}
                            rotate={rotateLabels}
                            color={chart.color(item.color)}
                            format={withUnit}
                          />
                        )) as LabelListContent
                      }
                    />
                  )}
                </Bar>
              ))}
              <Legend
                verticalAlign="bottom"
                height={28}
                iconType="circle"
                iconSize={9}
                formatter={(value) => <span style={{ color: LABEL_TEXT_COLOR }}>{value}</span>}
              />
            </BarChart>
          </Chart.Root>
        )}
      </VStack>
    </Box>
  )
}

/**
 * Render a single graph via the correct chart variant (phase / runtime / bar),
 * wrapped in a card tagged for the PDF exporter. The `data-*` attributes let
 * {@link rasterizeCharts} find each chart's `<svg>` and carry its title + section
 * into the exported document.
 */
function GraphRegion({
  graph,
  index,
  section,
  frequency,
}: {
  graph: Graph
  index: number
  section: string
  frequency: Frequency
}) {
  return (
    <RegionBoundary key={`${graph.title ?? "graph"}-${index}`} label="graph">
      <Box
        height="100%"
        {...{
          [CHART_CARD_ATTR]: "",
          [CHART_TITLE_ATTR]: graph.title ?? "",
          [CHART_SECTION_ATTR]: section,
        }}
      >
        {isRunHoursGraph(graph) ? (
          <RunHoursChart graph={graph} frequency={frequency} />
        ) : isPhaseShareGraph(graph) ? (
          <PhaseBalanceChart graph={graph} frequency={frequency} />
        ) : (
          <ReportGraphChart graph={graph} frequency={frequency} />
        )}
      </Box>
    </RegionBoundary>
  )
}

/**
 * Lay a group of graphs out in a responsive two-up grid so the report fits on
 * one A4-proportioned page. On narrow viewports the grid collapses to one
 * column; from `md` up graphs sit two per row. A lone graph spans the full row.
 */
function GraphGrid({ graphs, section, frequency }: { graphs: Graph[]; section: string; frequency: Frequency }) {
  if (graphs.length === 0) return null
  return (
    <Box
      display="grid"
      gridTemplateColumns={{ base: "1fr", md: graphs.length === 1 ? "1fr" : "1fr 1fr" }}
      gap={4}
      width="100%"
    >
      {graphs.map((graph, index) => (
        <GraphRegion
          key={`${graph.title ?? "graph"}-${index}`}
          graph={graph}
          index={index}
          section={section}
          frequency={frequency}
        />
      ))}
    </Box>
  )
}

/* -------------------------------------------------------------------------- */
/* Numbered report sections                                                   */
/* -------------------------------------------------------------------------- */

/**
 * A numbered report section: a large muted ordinal ("01") beside a section
 * title, over the section's content. Mirrors the mockup's numbered-section
 * narrative so the report reads as a structured document rather than a flat
 * stack of charts.
 */
function NumberedSection({
  ordinal,
  title,
  children,
}: {
  ordinal: string
  title: string
  children: React.ReactNode
}) {
  return (
    <Box as="section" width="100%">
      <Box display="flex" alignItems="baseline" gap={3} mb={3}>
        <Text fontSize="2xl" fontWeight="bold" color="#c9c9c9" lineHeight="1">
          {ordinal}
        </Text>
        <Heading as="h2" size="md" color="#1a1a1a">
          {title}
        </Heading>
      </Box>
      {/* Separator beneath the section heading. */}
      <Box borderBottomWidth="1px" borderBottomColor="#e2e2e2" mb={4} width="100%" />
      {children}
    </Box>
  )
}

/**
 * True when a phase-balance graph carries *meaningful* phase data: at least one
 * bucket with two or more present (non-null) phase legs. A single-phase asset
 * (only ever L1 present) or an all-null series is not worth a phase-balance
 * chart — the "Phase balance" section is suppressed for those so the report
 * doesn't show a flat/degenerate stacked bar (user request).
 */
function hasMultiPhaseData(graph: Graph): boolean {
  const series = (graph.series ?? []).filter((s) => PHASE_SHARE_KEYS.has(s.key))
  if (series.length < 2) return false
  // Count, per time bucket, how many phase legs have a present value.
  const presentByBucket = new Map<string, number>()
  for (const s of series) {
    for (const point of s.points ?? []) {
      if (point.value != null) {
        presentByBucket.set(point.time, (presentByBucket.get(point.time) ?? 0) + 1)
      }
    }
  }
  return Array.from(presentByBucket.values()).some((count) => count >= 2)
}

/** The report's graph-bearing sections, in document order. */
type SectionKey = "profile" | "runtime" | "balance" | "storage"

/**
 * Map a graph's backend `category` to its report section. The backend tags every
 * graph with a semantic category (energy, power, runtime, phase, level, …); the
 * report groups those into four generic sections so any entity type — hybrid,
 * system, battery-only, electrolyser — slots in without title/key guessing:
 *
 *  - **Profile**  — the electrical draw/generation/storage story
 *                   (power, energy, state of charge, production).
 *  - **Runtime**  — the engine/fuel/runtime story (fuel, engine-vs-battery,
 *                   generator runtime).
 *  - **Balance**  — per-phase distribution (load / genset phase balance).
 *  - **Storage**  — everything else: tank/sensor metrics (level, temperature,
 *                   pressure, weight, flow) and the catch-all for any unknown or
 *                   untagged category, so a graph is never silently dropped.
 */
const CATEGORY_SECTION: Record<string, SectionKey> = {
  power: "profile",
  energy: "profile",
  battery: "profile",
  production: "profile",
  fuel: "runtime",
  runtime: "runtime",
  phase: "balance",
  level: "storage",
  temperature: "storage",
  pressure: "storage",
  weight: "storage",
  flow: "storage",
  other: "storage",
}

/** Resolve a graph's section from its category, defaulting to the Storage catch-all. */
function sectionForGraph(graph: Graph): SectionKey {
  const category = (graph.category ?? "").toLowerCase()
  return CATEGORY_SECTION[category] ?? "storage"
}

/**
 * Drop degenerate single-phase (or all-null) phase-share graphs *before*
 * sectioning, gated purely on {@link isPhaseShareGraph} rather than the backend
 * `category`. Suppression must not depend on the graph being tagged
 * `category:"phase"`: a `phase_share` graph tagged with any other category (or
 * untagged) still routes to {@link PhaseBalanceChart} via {@link isPhaseShareGraph}
 * in {@link GraphRegion}, so a single-phase stacked bar could otherwise render
 * from the Storage catch-all. Filtering here suppresses it wherever it lands.
 */
function suppressSinglePhaseGraphs(graphs: Graph[]): Graph[] {
  return graphs.filter((graph) => !isPhaseShareGraph(graph) || hasMultiPhaseData(graph))
}

/**
 * Split the report's graphs into the four document sections by their backend
 * `category` (see {@link CATEGORY_SECTION}). Single-phase/empty phase-share
 * graphs are dropped up front (see {@link suppressSinglePhaseGraphs}) regardless
 * of how they're categorised, so the Balance section appears only when there's
 * more than one phase to show and no degenerate stacked bar can leak through a
 * mis-tag. Order within each section is preserved from the backend.
 */
function partitionGraphs(graphs: Graph[]): Record<SectionKey, Graph[]> {
  const sections: Record<SectionKey, Graph[]> = {
    profile: [],
    runtime: [],
    balance: [],
    storage: [],
  }
  for (const graph of suppressSinglePhaseGraphs(graphs)) {
    sections[sectionForGraph(graph)].push(graph)
  }
  return sections
}

/**
 * The graph sections in document order, with their display titles. Shared by the
 * on-screen render and the PDF export so both number/title the sections
 * identically (and the PDF's tagged chart cards line up by section title).
 */
const GRAPH_SECTIONS: Array<{ key: SectionKey; title: string }> = [
  { key: "profile", title: "Profile" },
  { key: "runtime", title: "Operations" },
  { key: "balance", title: "Phase balance" },
  { key: "storage", title: "Storage" },
]

/* -------------------------------------------------------------------------- */
/* Wide table (banded)                                                        */
/* -------------------------------------------------------------------------- */


/* -------------------------------------------------------------------------- */
/* States                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * The report surface: the muted canvas + optional branded chrome (header,
 * controls, period block) wrapping whatever body a given state renders.
 *
 * Every state — loading skeleton, error, unavailable, and the full report —
 * renders through this shell, so the header and the cadence/period controls
 * stay put across all of them (the controls never appear to "pop in" once data
 * loads). The `window` bounds come from `useReport` and are available even while
 * the first fetch is in flight, so the chrome shows the selected period over the
 * skeleton.
 */
function ReportShell({
  window,
  scope,
  controls,
  headerAction,
  entityName,
  cadenceLabel,
  containerRef,
  capturing,
  children,
}: {
  window: { start: string; stop: string }
  scope: ReportScopeInput
  controls?: React.ReactNode
  /**
   * Optional action rendered top-right of the report surface, aligned to the
   * page's right edge (e.g. the "Download PDF" button). Kept separate from
   * `controls` (the cadence/period selectors) so the selectors stay left and
   * the action sits on the right.
   */
  headerAction?: React.ReactNode
  entityName?: string
  cadenceLabel?: string
  containerRef?: React.Ref<HTMLDivElement>
  /**
   * True while the PDF export is rasterising charts. During capture the chart
   * cards are briefly resized to their A4 target widths (so Recharts re-lays-out
   * at a deterministic size); this raises an opaque cover over the surface so the
   * user sees a steady "Generating" state instead of the charts reflowing.
   */
  capturing?: boolean
  children: React.ReactNode
}) {
  return (
    <Box
      ref={containerRef}
      position="relative"
      bg="background"
      width="100%"
      px={{ base: 4, md: 6 }}
      // Top padding matches the space left before the report title below the
      // toolbar divider (the divider's mb={4}), so the toolbar's top breathing
      // room reads the same as the title's.
      pt={{ base: 3, md: 4 }}
      pb={{ base: 4, md: 6 }}
    >
      {/* Top toolbar: cadence/period selectors on the left, the right-aligned
          page action (Download PDF) on the right. Its heading→separator gap
          mirrors a NumberedSection's (mb={3}) so the toolbar reads as a peer of
          the numbered sections below. */}
      {(controls || headerAction) && (
        <Flex
          align="center"
          justify="space-between"
          gap={4}
          wrap="wrap"
          mb={3}
        >
          <Box minWidth={0}>{controls}</Box>
          {headerAction && <Box flexShrink={0}>{headerAction}</Box>}
        </Flex>
      )}
      {/* Thin divider separating the toolbar from the report. Its separator→body
          gap (mb={4}) matches a NumberedSection's separator, so the space down to
          the report title equals the space a section heading leaves before its
          content. */}
      {(controls || headerAction) && (
        <Box borderBottomWidth="1px" borderBottomColor="#e2e2e2" mb={4} width="100%" />
      )}
      {SHOW_REPORT_CHROME && (
        <Box mb={4}>
          <ReportChrome
            window={window}
            scope={scope}
            entityName={entityName}
            cadenceLabel={cadenceLabel}
          />
        </Box>
      )}
      {/* The report body, with a capture cover overlaid only over this region
          (not the toolbar/chrome above). While the export rasterises charts the
          body is briefly resized/reflowed; the cover reuses the report's own
          loading skeleton so it reads as a familiar "loading" state rather than a
          blank white screen. The cover is a sibling of the chart cards, so the
          per-card screenshot never captures it, and the "Generating" button
          spinner in the toolbar stays visible above it. */}
      <Box position="relative" width="100%">
        {children}
        {capturing && (
          <Box position="absolute" inset="0" bg="background" zIndex={2} overflow="hidden" aria-hidden="true">
            <ReportLoading />
          </Box>
        )}
      </Box>
    </Box>
  )
}

/** Skeleton body shown while the first fetch is in flight (inside the shell). */
function ReportLoading() {
  return (
    <VStack align="stretch" gap={6} width="100%">
      <FigureGrid loading />
      <Skeleton height="500px" width="100%" />
    </VStack>
  )
}

/** Copy for the "not available" state, differentiating the two reasons. */
function unavailableCopy(reason?: string | null): { heading: string; text: string } {
  if (reason === "not_applicable") {
    return {
      heading: "Report not available",
      text: "This entity doesn't support a performance report.",
    }
  }
  if (reason === "no_data") {
    return {
      heading: "Report not available",
      text: "There's no data for the selected period.",
    }
  }
  return { heading: "Report not available", text: "This report can't be shown right now." }
}

/* -------------------------------------------------------------------------- */
/* Document chrome                                                            */
/* -------------------------------------------------------------------------- */

/** The caption shown in-app beneath the period block (Requirement 7.3). */
const SAVINGS_CAPTION = "Savings measured against a diesel-only generator"

/**
 * Format an ISO 8601 timestamp for the document chrome (e.g. `3 Aug 2026`).
 * Falls back to the raw string if the value can't be parsed so the chrome never
 * throws on a malformed bound.
 */
function formatChromeDate(iso: string): string {
  try {
    return format(parseISO(iso), "d MMM yyyy")
  } catch {
    return iso
  }
}

/**
 * Format the report's reporting period for the chrome/PDF line. When the window
 * starts and ends on the same calendar day (e.g. a daily report) it collapses to
 * a single date ("15 Sep 2026") rather than repeating it as a range ("15 Sep
 * 2026 – 15 Sep 2026"). Multi-day windows keep the "start – end" range.
 */
function formatChromePeriod(start: string, stop: string): string {
  const from = formatChromeDate(start)
  const to = formatChromeDate(stop)
  return from === to ? from : `${from} – ${to}`
}

/**
 * Branded document chrome rendered above the KPI grid: a header identifying the
 * report, a reporting-period + issued-date block bound to the window bounds, and
 * the diesel-baseline savings caption (in-app only).
 *
 * A pure presenter: the period reflects the `window` bounds passed in — which
 * come straight from {@link useReport} — so it updates whenever the selected
 * window changes (Requirement 7.4). The issued date is the render/generation
 * date ("now").
 *
 * Requirements: 7.1 (branded header), 7.2 (period + issued block), 7.3
 * (caption), 7.4 (period reflects the selected window).
 */
/**
 * The report-title noun for each scope, so a system/installation report doesn't
 * read "asset". Falls back to "entity" for any unrecognised scope.
 */
const SCOPE_NOUN: Record<string, string> = {
  asset: "asset",
  // Hybrid assets report under the "asset" noun — the report title should read
  // "… asset report" rather than surfacing the "hybrid" class in the heading.
  hybrid: "asset",
  system: "system",
  installation: "installation",
}

/** Resolve the report-title noun for a scope (enum value or lowercase string). */
function scopeNoun(scope: ReportScopeInput): string {
  return SCOPE_NOUN[String(scope).toLowerCase()] ?? "entity"
}

/**
 * Build the report title.
 *
 * When an `entityName` is supplied the title leads with it — "Garic SES
 * performance report" — which is the primary form now that the report is named
 * per entity and the cadence lives in the period metadata line rather than the
 * title. When no name is available it falls back to the scope-based form
 * ("Asset performance report" / "System performance report" / …). The cadence
 * word is deliberately *not* part of the title: it's a first-class selector and
 * is shown alongside the date range instead (see {@link ReportChrome}).
 */
function reportTitle(scope: ReportScopeInput, entityName?: string): string {
  const trimmed = entityName?.trim()
  if (trimmed) return `${trimmed} performance report`
  const noun = scopeNoun(scope)
  const capitalisedNoun = noun.charAt(0).toUpperCase() + noun.slice(1)
  return `${capitalisedNoun} performance report`
}

function ReportChrome({
  window,
  scope,
  entityName,
  cadenceLabel,
}: {
  window: { start: string; stop: string }
  scope: ReportScopeInput
  entityName?: string
  cadenceLabel?: string
}) {
  const period = formatChromePeriod(window.start, window.stop)
  const issued = format(new Date(), "d MMM yyyy")
  // Lead the period line with the cadence when known ("Weekly · 8 Sep – …").
  const periodPrefix = cadenceLabel ? `${cadenceLabel} · ` : ""

  return (
    <Box as="header" width="100%">
      {/* Header: the report title on the left and a right metadata block
          (period/issued + caption) top-aligned so the title's line sits level
          with the period line ("Weekly · 14 Sep …") rather than being pushed
          down to the bottom of the two-line metadata block. The cadence/period
          selectors and the download action live in the toolbar above (see
          ReportShell). On mobile the two columns stack. */}
      <Box
        display="flex"
        flexDirection={{ base: "column", md: "row" }}
        alignItems={{ base: "flex-start", md: "flex-start" }}
        justifyContent="space-between"
        gap={{ base: 3, md: 4 }}
      >
        <Box display="flex" flexDirection="column" gap={3} minWidth={0}>
          <Heading as="h2" size="lg">
            {reportTitle(scope, entityName)}
          </Heading>
        </Box>
        <Box textAlign={{ base: "left", md: "right" }} flexShrink={0}>
          {/* Period + issued on one line; caption on a second. */}
          <Text fontSize="sm" color="#545454">
            {periodPrefix}
            {period} · Issued {issued}
          </Text>
          <Text fontSize="xs" color="#767676">
            {SAVINGS_CAPTION}
          </Text>
        </Box>
      </Box>
    </Box>
  )
}

/* -------------------------------------------------------------------------- */
/* Share control                                                              */
/* -------------------------------------------------------------------------- */

/**
 * How long the "Link copied" confirmation toast stays visible, in milliseconds.
 * Requirement 1.6 asks for at least 3 seconds; the clipboard-unavailable and
 * error toasts hold longer so the operator can read (and in the unavailable
 * case, hand-copy) the URL.
 */
const SHARE_TOAST_DURATION_MS = 4000

/**
 * Build the public share URL for a token, rooted at the app's base path.
 *
 * The hub mounts its router with `basename={import.meta.env.BASE_URL}`, which is
 * `/` in dev/prod but a nested path in preview deploys (the hub is built with
 * `--base /sections/pr/<slug>/hub/` and served from there). The `/share/:token`
 * route therefore lives under that base, so the copied link must include it —
 * a bare `${origin}/share/<token>` drops the base and 404s in preview.
 *
 * `import.meta.env.BASE_URL` always ends with a trailing slash, so joining it
 * with `share/<token>` and resolving against the origin via `new URL` yields the
 * correct absolute URL in every environment (and normalises any double slash).
 * Guarded with a `/` fallback so a non-Vite bundling of this shared module (no
 * `import.meta.env`) still produces a sane origin-rooted link.
 */
function buildShareUrl(token: string): string {
  const base = import.meta.env?.BASE_URL ?? "/"
  return new URL(`${base}share/${token}`, window.location.origin).toString()
}

/**
 * The live share link the toolbar is managing: its token id and current expiry
 * (`null` = no expiry). Threaded from the mint/reuse (ShareButton) and the
 * on-mount `getShareLink` fetch (ShareControl) into {@link SharePopover}, which
 * reflects the expiry in its UI (highlighting the active option and showing the
 * expiry date) and updates it in place when the operator changes it. `null`
 * overall means no link exists yet — the manage control stays disabled.
 */
interface ShareLinkState {
  id: string
  /** ISO 8601 expiry, or `null` for a link with no expiry. */
  expiresAt: string | null
}

/** Milliseconds in a day — used to turn an expiry-option's day count into a date. */
const MS_PER_DAY = 24 * 60 * 60 * 1000

/**
 * The expiry options offered in the manage popover. `days === null` is the
 * "No expiry" entry (clears any existing expiry via a null `expiresAt`); the
 * numeric entries compute `expiresAt = now + N days` at selection time
 * (Requirement 2.2, 2.3, 2.4).
 */
const EXPIRY_OPTIONS: ReadonlyArray<{ label: string; days: number | null }> = [
  { label: "No expiry", days: null },
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
]

/**
 * Which expiry option a link's current `expiresAt` corresponds to, so the
 * popover can mark it active. Matches the option whose computed `now + days`
 * lands within ~12h of the stored expiry (expiries are set as whole-day offsets
 * from "now", so day-granularity matching is exact enough and tolerant of the
 * elapsed time since the link was created). A link with no expiry matches the
 * "No expiry" option; an expiry that matches no preset (e.g. set in an older
 * build) highlights nothing.
 */
function activeExpiryLabel(expiresAt: string | null): string | null {
  if (expiresAt == null) return "No expiry"
  const target = new Date(expiresAt).getTime()
  if (Number.isNaN(target)) return null
  const now = Date.now()
  for (const option of EXPIRY_OPTIONS) {
    if (option.days == null) continue
    const expected = now + option.days * MS_PER_DAY
    if (Math.abs(expected - target) <= 12 * 60 * 60 * 1000) return option.label
  }
  return null
}

/** Write a URL to the clipboard, confirming with a toast, with a manual-copy
 *  fallback when the clipboard API is unavailable or blocked (Requirement
 *  1.5–1.7). */
async function copyShareUrl(url: string): Promise<void> {
  if (!navigator.clipboard?.writeText) {
    toaster.create({ title: "Share link", description: url, duration: SHARE_TOAST_DURATION_MS })
    return
  }
  try {
    await navigator.clipboard.writeText(url)
    toaster.success({ title: "Link copied", duration: SHARE_TOAST_DURATION_MS })
  } catch {
    toaster.create({ title: "Share link", description: url, duration: SHARE_TOAST_DURATION_MS })
  }
}

/**
 * Human one-liner describing the link's current expiry state for the popover
 * header. "Active · no expiry" for an open-ended link, "Active · expires 3 Aug
 * 2026" when dated. A malformed date degrades to the no-expiry phrasing rather
 * than throwing.
 */
function expiryStatusText(expiresAt: string | null): string {
  if (expiresAt == null) return "Active · no expiry"
  try {
    return `Active · expires ${format(parseISO(expiresAt), "d MMM yyyy")}`
  } catch {
    return "Active · no expiry"
  }
}

/**
 * One cell of the expiry segmented control.
 *
 * The background is resolved to a single literal colour in JS from the
 * active/hover/disabled state and applied as one `background` style prop, rather
 * than relying on a `bg` prop + `_hover` pseudo-rule. An earlier version did the
 * latter and the fill/hover didn't paint — a competing style won the cascade for
 * the plain `bg`, and `!important` on the base then shadowed the `:hover` rule.
 * Driving the colour from React state sidesteps CSS specificity entirely, so
 * both the active fill and the hover always render. The segment is a plain
 * `<button>` (no themed recipe) so nothing re-colours it behind our back.
 */
function ExpirySegment({
  label,
  isActive,
  isFirst,
  disabled,
  onClick,
}: {
  label: string
  isActive: boolean
  isFirst: boolean
  disabled: boolean
  onClick: () => void
}) {
  const [hovered, setHovered] = useState(false)

  const background = isActive
    ? hovered && !disabled
      ? "#0bc5ea" // active + hover
      : "#39aed2" // active
    : hovered && !disabled
      ? "#eaf7fa" // inactive + hover (pale blue)
      : "#ffffff" // inactive

  return (
    <chakra.button
      type="button"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      gap={1}
      cursor={disabled ? "not-allowed" : "pointer"}
      flex="1"
      minWidth={0}
      height="2.2em"
      px={1}
      borderLeftWidth={isFirst ? "0" : "1px"}
      borderLeftColor={isActive ? "#39aed2" : "#cde9f2"}
      color={isActive ? "white" : "#39aed2"}
      fontSize="0.78em"
      fontWeight={isActive ? "semibold" : "medium"}
      transition="background 0.12s ease, color 0.12s ease"
      opacity={disabled ? 0.7 : 1}
      style={{ background }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => !disabled && onClick()}
      disabled={disabled}
      aria-pressed={isActive}
    >
      {isActive && <FaCheck size={9} />}
      {label}
    </chakra.button>
  )
}

/**
 * The operator Share control: a Share button that opens a popover holding a
 * "Link sharing" switch, which is the single on/off for the entity's public link.
 *
 * The switch mirrors whether an active link exists (seeded on mount from
 * `getShareLink`), so it reads off by default and on once a link has been made:
 *   * **Off → on** mints the link (`createShareLink`) and reveals the rest of the
 *     popover: a one-line **status**, **Copy link**, and the **expiry**
 *     segmented control (None / 7d / 30d / 90d → `updateShareLink`).
 *   * **On → off** revokes the live link (`revokeShareLink`), but only after a
 *     confirm dialog, because anyone holding the link loses access. While off,
 *     only the switch is shown, so copy and expiry can't be reached.
 *
 * The backend keeps at most one active link per (tenant, scope, entity) —
 * `createShareLink` reuses it — so revoking the current link disables sharing
 * for the entity. Enabling and disabling each toast on success and failure; a
 * failure leaves the switch where it was. All mutations run on the `"primary"`
 * (experience) client.
 */
function ShareControl({ scope, entityId }: { scope: ReportScopeInput; entityId: string }) {
  const [open, setOpen] = useState(false)
  const [confirmingDisable, setConfirmingDisable] = useState(false)
  const [link, setLink] = useState<ShareLinkState | null>(null)

  const [createShareLink, { loading: creating }] = useMutationWithEndpoint<
    CreateShareLinkMutation,
    CreateShareLinkMutationVariables
  >(CreateShareLinkDocument, undefined, "primary")
  const [updateShareLink, { loading: updating }] = useMutationWithEndpoint<
    UpdateShareLinkMutation,
    UpdateShareLinkMutationVariables
  >(UpdateShareLinkDocument, undefined, "primary")
  const [revokeShareLink, { loading: revoking }] = useMutationWithEndpoint<
    RevokeShareLinkMutation,
    RevokeShareLinkMutationVariables
  >(RevokeShareLinkDocument, undefined, "primary")

  const { data: existingLink } = useQueryWithEndpoint<GetShareLinkQuery, GetShareLinkQueryVariables>(
    GetShareLinkDocument,
    {
      variables: { scope: toReportScope(scope), entityId },
      skip: !entityId,
      fetchPolicy: "cache-and-network",
    },
    "primary",
  )

  // Seed from the fetched link once. After that local state owns the link, so
  // neither a slower refetch nor a cache update (the revoke response rewrites
  // the cached link) can resurrect a link the operator just disabled.
  const seeded = useRef(false)
  useEffect(() => {
    const fetched = existingLink?.getShareLink
    if (fetched?.id && !seeded.current) {
      seeded.current = true
      setLink((current) => current ?? { id: fetched.id, expiresAt: fetched.expiresAt ?? null })
    }
  }, [existingLink])

  const enabled = link != null
  const busy = creating || updating || revoking
  const activeLabel = link ? activeExpiryLabel(link.expiresAt) : null

  async function enableSharing() {
    try {
      const { data } = await createShareLink({ variables: { scope: toReportScope(scope), entityId } })
      const token = data?.createShareLink.id ?? ""
      if (!token) throw new Error("No share link returned")
      setLink({ id: token, expiresAt: data?.createShareLink.expiresAt ?? null })
      toaster.success({ title: "Link sharing enabled", duration: SHARE_TOAST_DURATION_MS })
    } catch {
      toaster.error({
        title: "Couldn't enable link sharing",
        description: "The share link could not be created. Please try again.",
      })
    }
  }

  async function disableSharing() {
    if (!link) return
    try {
      await revokeShareLink({ variables: { id: link.id } })
      setLink(null)
      setConfirmingDisable(false)
      toaster.success({ title: "Link sharing disabled", duration: SHARE_TOAST_DURATION_MS })
    } catch {
      // Leave the link (and the switch) as they were and surface the failure.
      toaster.error({
        title: "Couldn't disable link sharing",
        description: "The share link could not be revoked. Please try again.",
      })
    }
  }

  // Turning on mints straight away. Turning off asks first: the popover closes
  // so the confirm dialog isn't stacked over it.
  function onToggle(checked: boolean) {
    seeded.current = true
    if (checked) {
      void enableSharing()
    } else {
      setOpen(false)
      setConfirmingDisable(true)
    }
  }

  async function onCopyLink() {
    if (link) await copyShareUrl(buildShareUrl(link.id))
  }

  async function onSelectExpiry(days: number | null) {
    if (!link) return
    // Requirement 2.3 / 2.4: expiresAt = now + N days (ISO), or null to clear.
    const expiresAt = days == null ? null : new Date(Date.now() + days * MS_PER_DAY).toISOString()
    try {
      await updateShareLink({ variables: { id: link.id, expiresAt } })
      setLink((current) => (current ? { ...current, expiresAt } : current))
      toaster.success({
        title: days == null ? "Expiry cleared" : "Expiry updated",
        duration: SHARE_TOAST_DURATION_MS,
      })
    } catch {
      // Requirement 2.6: leave state unchanged and surface the failure.
      toaster.error({
        title: "Couldn't update share link",
        description: "The expiry could not be updated. Please try again.",
      })
    }
  }

  const trigger = (
    <Button size="sm" variant="hydrologiq_secondary" flexShrink={0}>
      <FaShareNodes />
      Share
    </Button>
  )

  return (
    <>
      <Popover
        trigger={trigger}
        placement="bottom-end"
        open={open}
        onOpenChange={setOpen}
        closeOnOutside
        minWidth="268px"
      >
        <VStack align="stretch" gap={3} p={2}>
          <Switch
            checked={enabled}
            disabled={busy}
            onCheckedChange={(e) => onToggle(e.checked)}
            justifyContent="space-between"
            flexDirection="row-reverse"
            px={1}
          >
            <Text fontSize="sm" fontWeight="medium" color="primary">
              {creating ? "Enabling link sharing…" : enabled ? "Link sharing enabled" : "Link sharing disabled"}
            </Text>
          </Switch>

          {link && (
            <>
              <Box borderBottomWidth="1px" borderBottomColor="#e2e2e2" />

              {/* Status: a green dot + a plain-language summary of the live link. */}
              <HStack gap={2} px={1}>
                <Box as="span" boxSize="8px" borderRadius="full" bg="#10B981" flexShrink={0} />
                <Text fontSize="sm" color="fg.muted">
                  {expiryStatusText(link.expiresAt)}
                </Text>
              </HStack>

              {/* Copy link — re-copies without re-minting. Full-width secondary. */}
              <Button
                size="sm"
                variant="hydrologiq_secondary"
                justifyContent="center"
                onClick={onCopyLink}
                disabled={busy}
              >
                <FaLink />
                Copy link
              </Button>

              {/* Expiry — a compact segmented control. The active option is
                  filled; the rest are quiet buttons, so the row reads as one
                  control rather than four stacked form buttons. */}
              <Box>
                <Text px={1} pb={1.5} fontSize="xs" fontWeight="semibold" color="fg.muted" textTransform="uppercase" letterSpacing="0.03em">
                  Link expiry
                </Text>
                <HStack
                  gap={0}
                  borderWidth="1px"
                  borderColor="#39aed2"
                  borderRadius="6px"
                  overflow="hidden"
                >
                  {EXPIRY_OPTIONS.map((option, index) => {
                    const isActive = option.label === activeLabel
                    return (
                      <ExpirySegment
                        key={option.label}
                        label={option.days == null ? "None" : `${option.days}d`}
                        isActive={isActive}
                        isFirst={index === 0}
                        disabled={busy}
                        onClick={() => onSelectExpiry(option.days)}
                      />
                    )
                  })}
                </HStack>
              </Box>
            </>
          )}
        </VStack>
      </Popover>

      <Dialog
        title="Disable link sharing"
        open={confirmingDisable}
        setOpen={(isOpen) => {
          if (!isOpen) setConfirmingDisable(false)
        }}
        onClose={() => setConfirmingDisable(false)}
        loading={revoking}
        footer={
          <HStack gap={3}>
            <ActionButton
              onClick={() => setConfirmingDisable(false)}
              disabled={revoking}
              context={{ label: "Cancel", description: "Keep link sharing enabled", variant: "secondary" }}
            />
            <ActionButton
              onClick={() => void disableSharing()}
              loading={revoking}
              context={{ label: "Disable", description: "Revoke the current share link", variant: "warning" }}
            />
          </HStack>
        }
      >
        <Text>
          This will disable the current shared link. Anyone who has it will no longer be able to view this report.
        </Text>
      </Dialog>
    </>
  )
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Render the unified performance report for an entity.
 *
 * Drives {@link useReport} from its props and presents the resolved report:
 * loading skeleton → error → unavailable state → KPIs + graphs + wide table.
 * Each region is wrapped so a throwing KPI/graph/table degrades to a small
 * placeholder instead of blanking the whole view.
 */
export function Report({
  scope,
  entityId,
  start,
  stop,
  frequency = DEFAULT_FREQUENCY,
  showTrends = SHOW_TRENDS,
  showResultsTable = SHOW_RESULTS_TABLE,
  controls,
  entityName,
  cadenceLabel,
  cadence,
  enablePdfDownload = false,
  shareToken,
}: ReportProps) {
  const { report, window, error } = useReport(scope, entityId, start, stop, frequency, shareToken)

  // The entity name used to title the report. Prefer the caller-supplied prop
  // (the authed sections pass it from their own entity fetch); otherwise fall
  // back to the name the backend bakes into the report payload. The public
  // share view has no entity fetch of its own, so this fallback is what gives
  // its header the real "Garic SES performance report" title rather than the
  // generic scope-based one.
  const resolvedEntityName = entityName ?? report?.entityName ?? undefined

  // The capture container for the PDF export: the outer report surface. Owned
  // here (rather than passed in) so a single <Report enablePdfDownload> wires
  // the whole export without the caller managing a ref.
  const pdfContainerRef = useRef<HTMLDivElement>(null)

  // Build the PDF document content from the resolved report. Available report
  // data drives the KPI cards and the graph-section order the rasterised charts
  // slot into; the title/period line mirror the on-screen chrome. Empty until
  // the report resolves — the export button is disabled until then.
  const pdfContent = useMemo<ReportPdfContent>(() => {
    const kpiCards = (report?.kpis ?? []).map((kpi) => ({
      label: kpi.label,
      value: formatKpiValue(kpi),
      secondary: kpi.secondary,
      // Bake the trend string only when the on-screen path would show it too
      // (same `showTrends` toggle). `formatKpiTrend` returns null for a KPI with
      // no trend, so the PDF then renders no trend line for that card.
      trend: showTrends ? formatKpiTrend(kpi) : null,
    }))
    const periodPrefix = cadenceLabel ? `${cadenceLabel} · ` : ""
    const periodLine = `${periodPrefix}${formatChromePeriod(window.start, window.stop)} · Issued ${format(new Date(), "d MMM yyyy")}`
    // Graph section titles in document order — the same list the on-screen
    // report numbers, so the PDF's sections line up with the tagged chart cards.
    const sectionOrder = GRAPH_SECTIONS.map((s) => s.title)
    return {
      title: reportTitle(scope, resolvedEntityName),
      periodLine,
      caption: SAVINGS_CAPTION,
      kpis: kpiCards,
      sectionOrder,
    }
  }, [report, scope, resolvedEntityName, cadenceLabel, window.start, window.stop, showTrends])

  const {
    download: downloadPdf,
    generating: pdfGenerating,
  } = useReportPdf(pdfContainerRef, { entityName: resolvedEntityName, start, cadence }, pdfContent)

  // The Download PDF action, rendered right-aligned in the report toolbar
  // (separate from the caller's cadence/period selectors, which stay left).
  // Uses the primary button style. Disabled until the report is available
  // (nothing to capture before then).
  const canDownload = enablePdfDownload && report?.available === true

  // The header action group: the Share button sits beside the Download button
  // (Requirement 1.1). Share is always offered (it needs only scope + entityId,
  // which the component already has); Download is still gated on
  // `enablePdfDownload`. The group is omitted entirely when there is no entity
  // to share and no download — so a bare `<Report>` embed shows no actions.
  const downloadAction = enablePdfDownload ? (
    <Button
      size="sm"
      variant="hydrologiq_primary"
      onClick={downloadPdf}
      loading={pdfGenerating}
      loadingText="Generating"
      disabled={!canDownload}
      flexShrink={0}
    >
      <FaDownload />
      Download PDF
    </Button>
  ) : undefined
  // Hide the operator Share action in the public share view (Requirement 5.3):
  // a login-free viewer must not be able to re-share. Elsewhere Share is offered
  // whenever there is an entity to share.
  const shareAction = entityId && !shareToken ? <ShareControl scope={scope} entityId={entityId} /> : undefined
  const headerAction =
    shareAction || downloadAction ? (
      <HStack gap={2}>
        {shareAction}
        {downloadAction}
      </HStack>
    ) : undefined

  // Header/shell props shared by every ReportShell usage (loading / error /
  // unavailable / loaded), so the title, cadence line, and capture container are
  // identical throughout.
  const shellProps = {
    window,
    scope,
    controls,
    headerAction,
    entityName: resolvedEntityName,
    cadenceLabel,
    containerRef: pdfContainerRef,
    capturing: pdfGenerating,
  }

  // Every state renders through the shared shell so the header + cadence/period
  // controls stay put across loading, error, unavailable, and loaded (rather
  // than the controls popping in only once data arrives). `window` is available
  // from the hook even during the first fetch, so the chrome reflects the
  // selected period over the skeleton.
  if (error && !report) {
    // In the public share view a query error means the token is invalid,
    // revoked, or expired — show a neutral "link unavailable" state that
    // discloses nothing about the entity (Requirements 5.2, 3.5).
    const errorCopy = shareToken
      ? { heading: "Link unavailable", text: "This share link is no longer available." }
      : { heading: "Couldn't load report", text: "Something went wrong loading this report. Please try again." }
    // A dead share link has no report to re-range or download, so the public
    // view drops the date selector and Download toolbar here.
    const errorShellProps = shareToken ? { ...shellProps, controls: undefined, headerAction: undefined } : shellProps
    return (
      <ReportShell {...errorShellProps}>
        <Informative heading={errorCopy.heading} text={errorCopy.text} />
      </ReportShell>
    )
  }

  if (!report) {
    return (
      <ReportShell {...shellProps}>
        <ReportLoading />
      </ReportShell>
    )
  }

  if (!report.available) {
    const copy = unavailableCopy(report.unavailableReason)
    return (
      <ReportShell {...shellProps}>
        <Informative heading={copy.heading} text={copy.text} />
      </ReportShell>
    )
  }

  const kpis = report.kpis ?? []
  const graphs = report.graphs ?? []
  const columns = report.columns ?? []
  const rows = (report.rows ?? []) as Row[]

  const bySection = partitionGraphs(graphs)

  // Number the sections that actually have content, so the ordinals stay
  // sequential (01, 02, 03…) even when a section is empty for this entity.
  // Balance only appears when a phase graph has genuine multi-phase data
  // (partitionGraphs drops single-phase/empty phase graphs). Storage is the
  // catch-all for tank/sensor metrics and any untagged category.
  const sections: Array<{ title: string; node: React.ReactNode }> = []
  if (kpis.length > 0) {
    sections.push({
      title: "Headline",
      node: (
        <RegionBoundary label="metrics">
          <KpiCards kpis={kpis} showTrends={showTrends} />
        </RegionBoundary>
      ),
    })
  }
  for (const { key, title } of GRAPH_SECTIONS) {
    if (bySection[key].length > 0) {
      sections.push({ title, node: <GraphGrid graphs={bySection[key]} section={title} frequency={frequency} /> })
    }
  }

  return (
    // Full-width report surface (canvas + branded chrome + controls) shared with
    // every other state via ReportShell, so the header and selectors are
    // identical across loading/error/unavailable/loaded. White figure/graph
    // cards float on the muted canvas — the house pattern.
    <ReportShell {...shellProps}>
      <VStack align="stretch" gap={8} width="100%">
        {sections.map((section, index) => (
          <NumberedSection
            key={section.title}
            ordinal={String(index + 1).padStart(2, "0")}
            title={section.title}
          >
            {section.node}
          </NumberedSection>
        ))}

        {/* Results table hidden by default (showResultsTable → SHOW_RESULTS_TABLE
            = false). The table is aligned to the Download view's structure via
            ReportResultsTable and ready to re-enable; a caller can pass
            showResultsTable to opt in without a source edit. */}
        {showResultsTable && (
          <RegionBoundary label="table">
            <Box pb={10}>
              <Heading as="h3" size="md" mb={3}>
                Results
              </Heading>
              {columns.length === 0 || rows.length === 0 ? (
                <Text color="#545454">No table data for the selected period.</Text>
              ) : (
                <ReportResultsTable columns={columns} rows={rows} />
              )}
            </Box>
          </RegionBoundary>
        )}
      </VStack>
    </ReportShell>
  )
}

export default Report
